/**
 * Intelligence Dashboard Component
 * AI-powered analytics and insights panel
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Brain, TrendingUp, TrendingDown, AlertTriangle, 
  FileText, Clock, CheckCircle, RefreshCw, Shield,
  BarChart3, Target
} from 'lucide-react';
import agenticApi from '../../api/agenticApi';

const IntelligenceDashboard = ({ headers, timeRangeDays = 30 }) => {
  const [summary, setSummary] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [summaryRes, statsRes] = await Promise.all([
        agenticApi.getIntelligenceSummary(timeRangeDays, headers),
        agenticApi.getQuickStats(headers)
      ]);
      
      setSummary(summaryRes.data);
      setQuickStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch intelligence data:', err);
      setError(err.response?.data?.detail || 'Failed to load intelligence data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [timeRangeDays]);

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <span className="w-4 h-4 text-slate-400">—</span>;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[priority] || colors.low;
  };

  if (loading && !summary) {
    return (
      <Card className="border-purple-200">
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-3" />
          <p className="text-slate-600">Loading AI Intelligence...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6 text-center">
          <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <p className="text-red-700">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={fetchData}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="intelligence-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-slate-800">AI Intelligence Dashboard</h2>
          <Badge variant="outline" className="text-xs">
            Last {timeRangeDays} days
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      {quickStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <FileText className="w-4 h-4" />
                Total Documents
              </div>
              <div className="text-2xl font-bold text-slate-800">
                {quickStats.total_documents}
              </div>
            </CardContent>
          </Card>
          
          <Card className={quickStats.high_risk_documents > 0 ? 'border-red-200 bg-red-50/30' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <AlertTriangle className="w-4 h-4" />
                High Risk
              </div>
              <div className={`text-2xl font-bold ${quickStats.high_risk_documents > 0 ? 'text-red-600' : 'text-slate-800'}`}>
                {quickStats.high_risk_documents}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <Clock className="w-4 h-4" />
                Pending Workflows
              </div>
              <div className="text-2xl font-bold text-slate-800">
                {quickStats.pending_workflows}
              </div>
            </CardContent>
          </Card>
          
          <Card className={quickStats.overdue_workflows > 0 ? 'border-amber-200 bg-amber-50/30' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <Target className="w-4 h-4" />
                Health Score
              </div>
              <div className={`text-2xl font-bold ${
                quickStats.health_score >= 70 ? 'text-green-600' : 
                quickStats.health_score >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {quickStats.health_score}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recommendations */}
        {summary?.recommendations && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-600" />
                AI Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {summary.recommendations.map((rec, i) => (
                <div 
                  key={i}
                  className={`p-3 rounded-lg border ${getPriorityColor(rec.priority)}`}
                >
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="text-xs shrink-0">
                      {rec.priority.toUpperCase()}
                    </Badge>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{rec.title}</h4>
                      <p className="text-xs mt-1 opacity-80">{rec.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Risk Summary */}
        {summary?.risk_summary && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-600" />
                Risk Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Average Risk Score</span>
                  <span className="font-semibold">{summary.risk_summary.average_score}</span>
                </div>
                
                <div className="space-y-1">
                  {Object.entries(summary.risk_summary.distribution).map(([level, count]) => (
                    <div key={level} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">{level}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            level === 'Critical' ? 'bg-red-500' :
                            level === 'High' ? 'bg-orange-500' :
                            level === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ 
                            width: `${summary.document_insights?.total > 0 
                              ? (count / summary.document_insights.total) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                      <span className="text-xs text-slate-600 w-8">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trends */}
        {summary?.trends && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-purple-600" />
                Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div>
                    <div className="text-xs text-slate-500">Document Volume</div>
                    <div className="font-medium">
                      {summary.trends.document_volume.current_period} docs
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {getTrendIcon(summary.trends.document_volume.trend)}
                    <span className={`text-sm ${
                      summary.trends.document_volume.change_percent > 0 ? 'text-green-600' :
                      summary.trends.document_volume.change_percent < 0 ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {summary.trends.document_volume.change_percent > 0 ? '+' : ''}
                      {summary.trends.document_volume.change_percent}%
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div>
                    <div className="text-xs text-slate-500">Risk Trend</div>
                    <div className="font-medium">
                      Avg: {summary.trends.risk_trend.current_avg}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {getTrendIcon(summary.trends.risk_trend.trend)}
                    <Badge variant="outline" className="text-xs">
                      {summary.trends.risk_trend.trend}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delayed Workflows */}
        {summary?.workflow_insights?.most_delayed?.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                <Clock className="w-4 h-4" />
                Delayed Workflows
                <Badge className="bg-amber-100 text-amber-800 ml-auto">
                  {summary.workflow_insights.delayed_count}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {summary.workflow_insights.most_delayed.slice(0, 5).map((wf, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-2 bg-amber-50 rounded">
                    <span className="text-slate-700 truncate">{wf.current_step}</span>
                    <Badge variant="outline" className="text-xs text-red-600">
                      {wf.delay_days}d overdue
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default IntelligenceDashboard;
